/**
 * Canonical country registry for registration, pricing, and admin matrices.
 * ISO-style UN member list (193). Excludes Palestine and Holy See by policy.
 */

export const BILLABLE_LEVELS = ['foundation', 'certified', 'accredited'];

/** Maps legacy stored country strings to canonical dropdown keys. */
export const LEGACY_COUNTRY_ALIASES = {
  Congo: 'République du Congo | Republic of the Congo',
  'Saint Vincent': 'Saint Vincent and the Grenadines',
};

export const countryEndonyms = [
  'افغانستان | Afghanistan',
  'Shqipëria | Albania',
  'الجزائر | Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Հայաստան | Armenia',
  'Australia',
  'Österreich | Austria',
  'Azərbaycan | Azerbaijan',
  'Bahamas',
  'البحرين | Bahrain',
  'বাংলাদেশ | Bangladesh',
  'Barbados',
  'Беларусь | Belarus',
  'België | Belgium',
  'Belize',
  'Bénin | Benin',
  'འབྲུག་ཡུལ་ | Bhutan',
  'Bolivia',
  'Bosna i Hercegovina | Bosnia',
  'Botswana',
  'Brasil | Brazil',
  'Brunei',
  'България | Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde | Cape Verde',
  'កម្ពុជា | Cambodia',
  'Cameroun | Cameroon',
  'Canada',
  'République centrafricaine | Central African Republic',
  'Chile',
  '中国 | China',
  'Colombia',
  'Union des Comores | Comoros',
  'République du Congo | Republic of the Congo',
  'Costa Rica',
  "Côte d'Ivoire | Ivory Coast",
  'Hrvatska | Croatia',
  'Cuba',
  'Κύπρος | Cyprus',
  'Česko | Czechia',
  'Danmark | Denmark',
  'Deutschland | Germany',
  'Djibouti',
  'Dominica',
  'República Dominicana | Dominican Republic',
  'République démocratique du Congo | Democratic Republic of the Congo',
  'Ecuador',
  'مصر | Egypt',
  'El Salvador',
  'Guinea Ecuatorial | Equatorial Guinea',
  'Eritrea',
  'Eesti | Estonia',
  'Eswatini',
  'ኢትዮጵያ | Ethiopia',
  'Fiji',
  'Suomi | Finland',
  'France',
  'Gabon',
  'Gambia',
  'საქართველო | Georgia',
  'Ghana',
  'Ελλάδα | Greece',
  'Grenada',
  'Guatemala',
  'Guinée | Guinea',
  'Guiné-Bissau | Guinea-Bissau',
  'Guyana',
  'Haïti | Haiti',
  'Honduras',
  'Magyarország | Hungary',
  'Ísland | Iceland',
  'भारत | India',
  'Indonesia',
  'ایران | Iran',
  'العراق | Iraq',
  'Ireland',
  'Italia | Italy',
  'Jamaica',
  '日本 | Japan',
  'الأردن | Jordan',
  'Қазақстан | Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kosovo',
  '조선 | North Korea',
  '대한민국 | South Korea',
  'الكويت | Kuwait',
  'Кыргызстан | Kyrgyzstan',
  'Lao',
  'Latvija | Latvia',
  'لبنان | Lebanon',
  'Lesotho',
  'Liberia',
  'ليبيا | Libya',
  'Liechtenstein',
  'Lietuva | Lithuania',
  'Luxembourg',
  'Madagasikara | Madagascar',
  'Malawi',
  'Malaysia',
  'ދިވެހިރާއްޖެ | Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'موريتانيا | Mauritania',
  'Mauritius',
  'México | Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Монгол улс | Mongolia',
  'Crna Gora | Montenegro',
  'المغرب | Morocco',
  'Moçambique | Mozambique',
  'မြန်မာ | Myanmar',
  'Namibia',
  'Nauru',
  '네पाल | Nepal',
  'Nederland | Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Северна Македонија | North Macedonia',
  'Norge | Norway',
  'عمان | Oman',
  'پاکستان | Pakistan',
  'Belau | Palau',
  'Panamá | Panama',
  'Papua New Guinea',
  'Paraguay',
  'Perú | Peru',
  'Pilipinas | Philippines',
  'Polska | Poland',
  'Portugal',
  'قطر | Qatar',
  'România | Romania',
  'Россия | Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'São Tomé e Príncipe | São Tomé and Príncipe',
  'السعودية | Saudi Arabia',
  'Sénégal | Senegal',
  'Србија | Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovensko | Slovakia',
  'Slovenija | Slovenia',
  'Solomon Islands',
  'Soomaaliya | Somalia',
  'South Africa',
  'South Sudan',
  'España | Spain',
  'ශ්‍රී ලංකාව | Sri Lanka',
  'السودان | Sudan',
  'Suriname',
  'Sverige | Sweden',
  'Suisse | Switzerland',
  'سوريا | Syria',
  '台灣 | Taiwan',
  'Тоҷикистон | Tajikistan',
  'Tanzania',
  'ประเทศไทย | Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'تونس | Tunisia',
  'Türkiye | Turkey',
  'Türkmenistan | Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Україна | Ukraine',
  'الإمارات | UAE',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Oʻzbekiston | Uzbekistan',
  'Vanuatu',
  'Venezuela',
  'Việt Nam | Vietnam',
  'اليمن | Yemen',
  'Zambia',
  'Zimbabwe',
];

export const WORLD_BANK_TIERS = {
  'افغانستان | Afghanistan': 4,
  'Shqipëria | Albania': 2,
  'الجزائر | Algeria': 2,
  Andorra: 1,
  Angola: 3,
  'Antigua and Barbuda': 1,
  Argentina: 2,
  'Հայաստան | Armenia': 2,
  Australia: 1,
  'Österreich | Austria': 1,
  'Azərbaycan | Azerbaijan': 2,
  Bahamas: 1,
  'البحرين | Bahrain': 1,
  'বাংলাদেশ | Bangladesh': 3,
  Barbados: 1,
  'Беларусь | Belarus': 2,
  'België | Belgium': 1,
  Belize: 2,
  'Bénin | Benin': 3,
  'འབྲུག་ཡུལ་ | Bhutan': 3,
  Bolivia: 3,
  'Bosna i Hercegovina | Bosnia': 2,
  Botswana: 2,
  'Brasil | Brazil': 2,
  Brunei: 1,
  'България | Bulgaria': 1,
  'Burkina Faso': 4,
  Burundi: 4,
  'Cabo Verde | Cape Verde': 3,
  'កម្ពុជា | Cambodia': 3,
  'Cameroun | Cameroon': 3,
  Canada: 1,
  'République centrafricaine | Central African Republic': 4,
  Chile: 1,
  '中国 | China': 2,
  Colombia: 2,
  'Union des Comores | Comoros': 3,
  'République du Congo | Republic of the Congo': 3,
  'Costa Rica': 2,
  "Côte d'Ivoire | Ivory Coast": 3,
  'Hrvatska | Croatia': 1,
  Cuba: 2,
  'Κύπρος | Cyprus': 1,
  'Česko | Czechia': 1,
  'Danmark | Denmark': 1,
  'Deutschland | Germany': 1,
  Djibouti: 3,
  Dominica: 2,
  'República Dominicana | Dominican Republic': 2,
  'République démocratique du Congo | Democratic Republic of the Congo': 4,
  Ecuador: 2,
  'مصر | Egypt': 3,
  'El Salvador': 2,
  'Guinea Ecuatorial | Equatorial Guinea': 2,
  Eritrea: 4,
  'Eesti | Estonia': 1,
  Eswatini: 3,
  'ኢትዮጵያ | Ethiopia': 4,
  Fiji: 2,
  'Suomi | Finland': 1,
  France: 1,
  Gabon: 2,
  Gambia: 4,
  'საქართველო | Georgia': 2,
  Ghana: 3,
  'Ελλάδα | Greece': 1,
  Grenada: 2,
  Guatemala: 2,
  'Guinée | Guinea': 4,
  'Guiné-Bissau | Guinea-Bissau': 4,
  Guyana: 1,
  'Haïti | Haiti': 4,
  Honduras: 3,
  'Magyarország | Hungary': 1,
  'Ísland | Iceland': 1,
  'भारत | India': 3,
  Indonesia: 2,
  'ایران | Iran': 2,
  'العراق | Iraq': 2,
  Ireland: 1,
  'Italia | Italy': 1,
  Jamaica: 2,
  '日本 | Japan': 1,
  'الأردن | Jordan': 3,
  'Қазақстан | Kazakhstan': 2,
  Kenya: 3,
  Kiribati: 3,
  Kosovo: 3,
  '조선 | North Korea': 4,
  '대한민국 | South Korea': 1,
  'الكويت | Kuwait': 1,
  'Кыргызстан | Kyrgyzstan': 3,
  Lao: 3,
  'Latvija | Latvia': 1,
  'لبنان | Lebanon': 3,
  Lesotho: 3,
  Liberia: 4,
  'ليبيا | Libya': 2,
  Liechtenstein: 1,
  'Lietuva | Lithuania': 1,
  Luxembourg: 1,
  'Madagasikara | Madagascar': 4,
  Malawi: 4,
  Malaysia: 2,
  'ދިވެހިރާއްޖެ | Maldives': 2,
  Mali: 4,
  Malta: 1,
  'Marshall Islands': 2,
  'موريتانيا | Mauritania': 3,
  Mauritius: 1,
  'México | Mexico': 2,
  Micronesia: 3,
  Moldova: 2,
  Monaco: 1,
  'Монгол улс | Mongolia': 2,
  'Crna Gora | Montenegro': 2,
  'المغرب | Morocco': 3,
  'Moçambique | Mozambique': 4,
  'မြန်မာ | Myanmar': 3,
  Namibia: 2,
  Nauru: 1,
  '네पाल | Nepal': 3,
  'Nederland | Netherlands': 1,
  'New Zealand': 1,
  Nicaragua: 3,
  Niger: 4,
  Nigeria: 3,
  'Северна Македонија | North Macedonia': 2,
  'Norge | Norway': 1,
  'عمان | Oman': 1,
  'پاکستان | Pakistan': 3,
  'Belau | Palau': 1,
  'Panamá | Panama': 1,
  'Papua New Guinea': 3,
  Paraguay: 2,
  'Perú | Peru': 2,
  'Pilipinas | Philippines': 3,
  'Polska | Poland': 1,
  Portugal: 1,
  'قطر | Qatar': 1,
  'România | Romania': 1,
  'Россия | Russia': 1,
  Rwanda: 4,
  'Saint Kitts and Nevis': 1,
  'Saint Lucia': 2,
  'Saint Vincent and the Grenadines': 2,
  Samoa: 3,
  'San Marino': 1,
  'São Tomé e Príncipe | São Tomé and Príncipe': 3,
  'السعودية | Saudi Arabia': 1,
  'Sénégal | Senegal': 3,
  'Србија | Serbia': 2,
  Seychelles: 1,
  'Sierra Leone': 4,
  Singapore: 1,
  'Slovensko | Slovakia': 1,
  'Slovenija | Slovenia': 1,
  'Solomon Islands': 3,
  'Soomaaliya | Somalia': 4,
  'South Africa': 2,
  'South Sudan': 4,
  'España | Spain': 1,
  'ශ්‍රී ලංකාව | Sri Lanka': 3,
  'السودان | Sudan': 4,
  Suriname: 2,
  'Sverige | Sweden': 1,
  'Suisse | Switzerland': 1,
  'سوريا | Syria': 4,
  '台灣 | Taiwan': 1,
  'Тоҷикистон | Tajikistan': 3,
  Tanzania: 3,
  'ประเทศไทย | Thailand': 2,
  'Timor-Leste': 3,
  Togo: 4,
  Tonga: 2,
  'Trinidad and Tobago': 1,
  'تونس | Tunisia': 3,
  'Türkiye | Turkey': 2,
  'Türkmenistan | Turkmenistan': 2,
  Tuvalu: 2,
  Uganda: 4,
  'Україна | Ukraine': 2,
  'الإمارات | UAE': 1,
  'United Kingdom': 1,
  'United States': 1,
  Uruguay: 1,
  'Oʻzbekiston | Uzbekistan': 3,
  Vanuatu: 3,
  Venezuela: 3,
  'Việt Nam | Vietnam': 3,
  'اليمن | Yemen': 4,
  Zambia: 3,
  Zimbabwe: 3,
};

export function countryEnglishLabel(countryField) {
  const s = String(countryField || '').trim();
  if (!s) return '';
  const pipe = s.lastIndexOf('|');
  if (pipe >= 0) return s.slice(pipe + 1).trim();
  return s;
}

export function normalizeCountryKey(country) {
  const raw = String(country || '').trim();
  if (!raw) return '';
  if (countryEndonyms.includes(raw)) return raw;
  if (LEGACY_COUNTRY_ALIASES[raw]) return LEGACY_COUNTRY_ALIASES[raw];
  const alias = Object.entries(LEGACY_COUNTRY_ALIASES).find(([legacy]) => legacy === raw);
  if (alias) return alias[1];
  return raw;
}
